import { z } from "zod";
import { makeGetRequest } from "../../utils";
import { deviceUnionSchema, netWorkTypeUnionSchema } from "./listSessionUsers";

type GetSessionUserQosOptions = {
	/**
   * The session's ID. If the ID begins with a / character or contains // characters, you must double-encode the ID value.
Examples: KkMHZ4y8QhSUWAHi5sWvfg== */
	sessionId: string;
	/**
	 * The user's ID.
Examples: DYHrdpjrS3uaOf7dPkkg8w
	 */
	userId: string;
	/**
	 *Default: live
Allowed: past ┃ live
The type of session to query:

past — A past session that occurred during the specified date range.
live — A live sessions.
This parameter defaults to live.

Examples: past
	 */
	type?: "past" | "live";
};

const QosObject = z.object({
	bitrate: z.string(),
	latency: z.string(),
	jitter: z.string(),
	avg_loss: z.string(),
	max_loss: z.string(),
});

const VideoQosObject = QosObject.extend({
	resolution: z.string(),
	frame_rate: z.string(),
});

const CpuUsage = z.object({
	zoom_min_cpu_usage: z.string(),
	zoom_avg_cpu_usage: z.string(),
	zoom_max_cpu_usage: z.string(),
	system_max_cpu_usage: z.string(),
});

const SessionUserQoSSchema = z
	.object({
		id: z.string(),
		name: z.string(),
		device: deviceUnionSchema,
		ip_address: z.string(),
		location: z.string(),
		network_type: netWorkTypeUnionSchema,
		microphone: z.string(),
		speaker: z.string(),
		camera: z.string(),
		data_center: z.string(),
		connection_type: z.string(),
		join_time: z.string(),
		leave_time: z.string(),
		user_qos: z.array(
			z.object({
				date_time: z.string(),
				audio_input: QosObject,
				audio_output: QosObject,
				video_input: VideoQosObject,
				video_output: VideoQosObject,
				as_input: VideoQosObject,
				as_output: VideoQosObject,
				audio_device_from_rwg: QosObject,
				audio_device_to_rwg: QosObject,
				video_device_from_rwg: VideoQosObject,
				video_device_to_rwg: VideoQosObject,
				as_device_from_rwg: VideoQosObject,
				as_device_to_rwg: VideoQosObject,
				cpu_usage: CpuUsage,
			}),
		),
	})
	.deepPartial();

type SessionUserQoS = z.infer<typeof SessionUserQoSSchema>;
const getSessionUserQos = async (
	token: string,
	options: GetSessionUserQosOptions,
): Promise<SessionUserQoS> => {
	const res = await makeGetRequest({
		token,
		pathList: [
			"v2",
			"videosdk",
			"sessions",
			options.sessionId,
			"users",
			options.userId,
			"qos",
		],
		options: { ...options, type: options.type ?? "live" },
	});
	const parsed = SessionUserQoSSchema.parse(res);
	return parsed;
};

export const curriedGetSessionUserQos = (token: string) => (
	options: GetSessionUserQosOptions,
) => getSessionUserQos(token, options);
